import {
    pickMilestoneTimingRule,
    resolveMilestoneExpectedDate,
} from "@common/helpers/domains/accounting/scheduleMilestones";
import {
    MilestoneExampleDate,
    SCHEDULE_MILESTONES_MUST_SUM_MESSAGE,
} from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import type { ZodIssue } from "zod";
import type { ScheduleComponentForm } from "./scheduleFormHelpers";
import type { ScheduleMilestoneForm } from "./scheduleMilestoneFormHelpers";
import {
    MILESTONE_DATE_REQUIRED_MESSAGE,
    MILESTONE_DESCRIPTION_REQUIRED_MESSAGE,
    milestonePlanSchema,
    milestoneTimingFormSchema,
} from "./scheduleMilestoneRow.schema";

export interface MilestoneExampleDateForm {
    key: string;
    expectedDate: string;
}

export const MILESTONE_PERCENTS_TOTAL_MESSAGE =
    "Milestone percents must total 100%";

/** The preview's wording where it differs from the row's own message. */
const PREVIEW_COPY_BY_ROW_MESSAGE: Record<string, string> = {
    [MILESTONE_DESCRIPTION_REQUIRED_MESSAGE]: "Add a description",
    [MILESTONE_DATE_REQUIRED_MESSAGE]: "Select a specific date",
};

const PREVIEW_COPY_BY_PLAN_MESSAGE: Record<string, string> = {
    [SCHEDULE_MILESTONES_MUST_SUM_MESSAGE]: MILESTONE_PERCENTS_TOTAL_MESSAGE,
};

function isMilestoneRowIssue(issue: ZodIssue): boolean {
    return issue.path[0] === "milestones" && typeof issue.path[1] === "number";
}

export function hasValidExampleAmount(exampleAmount: number): boolean {
    return Number.isFinite(exampleAmount) && exampleAmount > 0;
}

function describeMilestoneIssue(issue: ZodIssue): string {
    const [, rowIndex] = issue.path;
    if (isMilestoneRowIssue(issue) && typeof rowIndex === "number") {
        const copy =
            PREVIEW_COPY_BY_ROW_MESSAGE[issue.message] ?? issue.message;
        return `${copy} for milestone ${rowIndex + 1}`;
    }
    return PREVIEW_COPY_BY_PLAN_MESSAGE[issue.message] ?? issue.message;
}

/**
 * What the preview still needs, or null once it can run: the first issue
 * the plan schema reports, in row order, in the preview's own words.
 */
export function buildMilestonePreviewUnavailableMessage(values: {
    components: ReadonlyArray<
        Pick<ScheduleComponentForm, "method" | "allocationPercent">
    >;
    milestones: readonly ScheduleMilestoneForm[];
    exampleAmount: number;
}): string | null {
    const result = milestonePlanSchema.safeParse({
        components: values.components,
        milestones: values.milestones,
    });
    if (!result.success) {
        const issue =
            result.error.issues.find(isMilestoneRowIssue) ??
            result.error.issues[0];
        return describeMilestoneIssue(issue);
    }
    if (!hasValidExampleAmount(values.exampleAmount)) {
        return "Enter an example amount";
    }
    return null;
}

/**
 * The day each milestone picker shows: the typed example day, else the
 * row's rule resolved from the example source date, else nothing while the
 * row's timing is incomplete.
 */
export function resolveMilestoneExampleDates(options: {
    milestones: readonly ScheduleMilestoneForm[];
    sourceDate: string;
    exampleDates: readonly MilestoneExampleDateForm[];
}): Array<string | undefined> {
    const exampleDateByKey = new Map(
        options.exampleDates.map((example) => [
            example.key,
            example.expectedDate,
        ]),
    );
    return options.milestones.map((milestone) => {
        const exampleDate = exampleDateByKey.get(milestone.key);
        if (exampleDate !== undefined) {
            return exampleDate;
        }
        if (!milestoneTimingFormSchema.safeParse(milestone).success) {
            return undefined;
        }
        return resolveMilestoneExpectedDate({
            milestone,
            sourceDate: options.sourceDate,
        });
    });
}

/** The preview body's overrides: row keys become 1-based sort orders; stale keys drop. */
export function toMilestoneExampleDatesBody(options: {
    milestones: ReadonlyArray<{ key: string }>;
    exampleDates: readonly MilestoneExampleDateForm[];
}): MilestoneExampleDate[] | undefined {
    const sortOrderByKey = new Map(
        options.milestones.map((milestone, index) => [
            milestone.key,
            index + 1,
        ]),
    );
    const body = options.exampleDates.flatMap((example) => {
        const sortOrder = sortOrderByKey.get(example.key);
        return sortOrder === undefined
            ? []
            : [{ sortOrder, expectedDate: example.expectedDate }];
    });
    return body.length === 0 ? undefined : body;
}

export function setMilestoneExampleDate(options: {
    exampleDates: readonly MilestoneExampleDateForm[];
    key: string;
    expectedDate: string | undefined;
}): MilestoneExampleDateForm[] {
    const others = options.exampleDates.filter(
        (example) => example.key !== options.key,
    );
    if (options.expectedDate === undefined) {
        return others;
    }
    return [
        ...others,
        { key: options.key, expectedDate: options.expectedDate },
    ];
}

function isSameTimingRule(
    left: ScheduleMilestoneForm,
    right: ScheduleMilestoneForm,
): boolean {
    const leftRule = pickMilestoneTimingRule(left);
    const rightRule = pickMilestoneTimingRule(right);
    return (
        leftRule.anchor === rightRule.anchor &&
        leftRule.offsetCount === rightRule.offsetCount &&
        leftRule.offsetUnit === rightRule.offsetUnit &&
        leftRule.specificDate === rightRule.specificDate
    );
}

/** A typed example day belongs to the rule it was typed for: a changed rule drops it. */
export function dropStaleMilestoneExampleDates(options: {
    previousMilestones: readonly ScheduleMilestoneForm[];
    milestones: readonly ScheduleMilestoneForm[];
    exampleDates: readonly MilestoneExampleDateForm[];
}): MilestoneExampleDateForm[] {
    const previousByKey = new Map(
        options.previousMilestones.map((milestone) => [
            milestone.key,
            milestone,
        ]),
    );
    const unchangedKeys = new Set(
        options.milestones
            .filter((milestone) => {
                const previous = previousByKey.get(milestone.key);
                return (
                    previous !== undefined &&
                    isSameTimingRule(previous, milestone)
                );
            })
            .map((milestone) => milestone.key),
    );
    return options.exampleDates.filter((example) =>
        unchangedKeys.has(example.key),
    );
}
